import type { MetricLatestResponse } from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useMetricLatest } from '../_sdk/hooks';
import { defineWidget } from '../_sdk/types';

/**
 * Service up/down matrix (PRD VS-042): the newest value of every series of an
 * up/down metric — `uptime.up` from Uptime Kuma, or any 0/1 metric. Bound by
 * props, never to a source type (§9.2 rule 3).
 */

export const PropsSchema = z.object({
  label: z.string().max(80).optional(),
  sourceId: z.uuid(),
  metricName: z.string().min(1).max(200).default('uptime.up'),
  /** The tag naming each cell, e.g. `monitor`. Empty: every tag value, joined. */
  labelTag: z.string().max(64).default('monitor'),
  /** Up when the value is at least this. */
  upAtLeast: z.number().default(1),
});

export type StatusGridProps = z.output<typeof PropsSchema>;
export type StatusGridData = MetricLatestResponse['items'];

export default defineWidget<StatusGridProps, StatusGridData>({
  type: 'status-grid',
  category: 'metric',
  defaultSize: { w: 4, h: 3 },
  minSize: { w: 2, h: 2 },
  propsSchema: PropsSchema,
  defaults: { metricName: 'uptime.up', labelTag: 'monitor', upAtLeast: 1 },
  sourceIds: (p) => [p.sourceId],
  useData: (p) =>
    toDataState(useMetricLatest(p.sourceId, p.metricName), {
      select: (r) => r.items,
      isEmpty: (items) => items.length === 0,
      lastUpdated: (items) => {
        const newest = Math.max(0, ...items.map((i) => Date.parse(i.ts)));
        return newest > 0 ? new Date(newest) : undefined;
      },
    }),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
